/**
 * IndeMenu Utility Helpers
 * Contains utility functions for common operations across the application
 */

/**
 * Generates a unique identifier with optional entity prefix
 * @param {string} entityType - Optional entity type prefix (e.g., 'order', 'user')
 * @param {boolean} includeTimestamp - Whether to include timestamp in ID for sorting
 * @returns {string} A unique identifier
 */
export const generateUniqueId = (entityType?: string, includeTimestamp = true): string => {
    // Generate random components
    const randomPart = Math.random().toString(36).substring(2, 10);
    const secondRandomPart = Math.random().toString(36).substring(2, 6);

    // Create timestamp component (optional)
    const timestamp = includeTimestamp ? Date.now().toString(36) : '';

    // Create prefix based on entity type
    let prefix = '';
    if (entityType) {
        switch (entityType.toLowerCase()) {
            case 'order':
            case 'orders':
                prefix = 'ord-';
                break;
            case 'user':
            case 'users':
            case 'customer':
                prefix = 'usr-';
                break;
            case 'restaurant':
            case 'restaurants':
                prefix = 'res-';
                break;
            case 'item':
            case 'menu-item':
                prefix = 'itm-';
                break;
            case 'booking':
            case 'reservation':
                prefix = 'bkg-';
                break;
            case 'payment':
            case 'transaction':
                prefix = 'pay-';
                break;
            case 'cart':
                prefix = 'crt-';
                break;
            case 'review':
                prefix = 'rev-';
                break;
            case 'table':
                prefix = 'tbl-';
                break;
            default:
                // Use first 3 chars of entityType as prefix
                prefix = `${entityType.substring(0, 3).toLowerCase()}-`;
        }
    }

    return `${prefix}${timestamp}${timestamp ? '-' : ''}${randomPart}-${secondRandomPart}`;
};

/**
 * Generates a short, readable reference code for user-facing displays
 * @param {string} entityType - Type of entity for the reference code
 * @returns {string} A short reference code (e.g., "ORD-4X7B")
 */
export const generateReferenceCode = (entityType: string): string => {
    const prefix = entityType.substring(0, 3).toUpperCase();
    const randomPart = Math.random().toString(36).substring(2, 6).toUpperCase();

    return `${prefix}-${randomPart}`;
};

/**
 * Generates a sequential order number with date component
 * Format: YYYYMMDD-XXXX (e.g., 20250811-0001)
 * @param {number} sequence - Current sequence number (usually from DB)
 * @returns {string} Formatted order number
 */
export const generateOrderNumber = (sequence: number): string => {
    const now = new Date();
    const dateComponent = now.getFullYear() +
        String(now.getMonth() + 1).padStart(2, '0') +
        String(now.getDate()).padStart(2, '0');

    const sequenceComponent = String(sequence).padStart(4, '0');

    return `${dateComponent}-${sequenceComponent}`;
};

/**
 * Formats a date according to the specified format
 * @param {Date | string | number} date - The date to format
 * @param {string} format - The format to use (default: 'YYYY-MM-DD HH:mm')
 * @returns {string} Formatted date string
 */
export const formatDate = (date: Date | string | number, format = 'YYYY-MM-DD HH:mm'): string => {
    const d = typeof date === 'object' ? date : new Date(date);

    // Handle invalid dates
    if (isNaN(d.getTime())) {
        return 'Invalid date';
    }

    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const seconds = String(d.getSeconds()).padStart(2, '0');

    // Replace format tokens with actual values
    return format
        .replace('YYYY', String(year))
        .replace('MM', month)
        .replace('DD', day)
        .replace('HH', hours)
        .replace('mm', minutes)
        .replace('ss', seconds);
};

/**
 * Returns current date and time in ISO format
 * @returns {string} Current date and time in ISO format
 */
export const getCurrentISODateTime = (): string => {
    return new Date().toISOString();
};

/**
 * Formats a currency value
 * @param {number} amount - The amount to format
 * @param {string} currencyCode - Currency code (default: 'MWK' for Malawian Kwacha)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount: number, currencyCode = 'MWK'): string => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 2
    }).format(amount);
};

/**
 * Truncates text to a specified length and adds ellipsis if necessary
 * @param {string} text - The text to truncate
 * @param {number} maxLength - Maximum length before truncation
 * @returns {string} Truncated text
 */
export const truncateText = (text: string, maxLength: number): string => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
};

/**
 * Converts a string to title case
 * @param {string} str - The string to convert
 * @returns {string} Title-cased string
 */
export const toTitleCase = (str: string): string => {
    return str.replace(
        /\w\S*/g,
        txt => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase()
    );
};

/**
 * Calculates time difference and returns a human-readable string
 * @param {Date | string | number} date - The date to compare with current time
 * @returns {string} Human-readable time difference
 */
export const timeAgo = (date: Date | string | number): string => {
    const now = new Date();
    const pastDate = new Date(date);
    const diff = now.getTime() - pastDate.getTime();

    // Convert milliseconds to appropriate units
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);

    // Return appropriate string based on the time difference
    if (years > 0) return `${years} ${years === 1 ? 'year' : 'years'} ago`;
    if (months > 0) return `${months} ${months === 1 ? 'month' : 'months'} ago`;
    if (days > 0) return `${days} ${days === 1 ? 'day' : 'days'} ago`;
    if (hours > 0) return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
    if (minutes > 0) return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
    return 'just now';
};

/**
 * Calculates estimated delivery time based on preparation time and distance
 * @param {number} prepTimeMinutes - Preparation time in minutes
 * @param {number} distanceKm - Distance in kilometers
 * @returns {number} Estimated delivery time in minutes
 */
export const calculateEstimatedDeliveryTime = (prepTimeMinutes: number, distanceKm: number): number => {
    // Assume average speed of 30 km/h = 0.5 km/min
    const travelTimeMinutes = distanceKm / 0.5;

    // Add buffer time (5 min) for handling, packaging, etc.
    return Math.ceil(prepTimeMinutes + travelTimeMinutes + 5);
};

/**
 * Validate phone number format (simple check)
 * @param {string} phoneNumber - Phone number to validate
 * @returns {boolean} True if valid format
 */
export const isValidPhoneNumber = (phoneNumber: string): boolean => {
    // Basic validation for Malawian numbers (starts with +265 or 0, followed by 9 digits)
    const phoneRegex = /^(\+265|0)[0-9]{9}$/;
    return phoneRegex.test(phoneNumber);
};

/**
 * Validate email format
 * @param {string} email - Email to validate
 * @returns {boolean} True if valid format
 */
export const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

/**
 * Check if a date is today
 * @param {Date | string | number} date - The date to check
 * @returns {boolean} True if date is today
 */
export const isToday = (date: Date | string | number): boolean => {
    const today = new Date();
    const checkDate = new Date(date);

    return (
        checkDate.getDate() === today.getDate() &&
        checkDate.getMonth() === today.getMonth() &&
        checkDate.getFullYear() === today.getFullYear()
    );
};

/**
 * Debounce function to limit how often a function can be called
 * @param {Function} func - Function to debounce
 * @param {number} wait - Milliseconds to wait
 * @returns {Function} Debounced function
 */
export const debounce = <T extends (...args: any[]) => any>(func: T, wait: number): ((...args: Parameters<T>) => void) => {
    let timeout: NodeJS.Timeout | null = null;

    return (...args: Parameters<T>): void => {
        if (timeout) clearTimeout(timeout);

        timeout = setTimeout(() => {
            func(...args);
        }, wait);
    };
};

/**
 * Sleep/wait utility for async operations
 * @param {number} ms - Milliseconds to sleep
 * @returns {Promise<void>}
 */
export const sleep = (ms: number): Promise<void> => {
    return new Promise(resolve => setTimeout(resolve, ms));
};

/**
 * Generate a verification code
 * @param {number} length - Length of code (default: 6)
 * @returns {string} Numeric verification code
 */
export const generateVerificationCode = (length: number = 6): string => {
    return Math.floor(Math.random() * Math.pow(10, length))
        .toString()
        .padStart(length, '0');
};

/**
 * Gets initials from a name
 * @param {string} name - Full name
 * @returns {string} Initials (up to 2 characters)
 */
export const getInitials = (name: string): string => {
    if (!name) return '';

    const parts = name.trim().split(/\s+/);

    if (parts.length === 1) {
        return parts[0].substring(0, 2).toUpperCase();
    }

    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

/**
 * Creates a slug from a string
 * @param {string} str - String to convert to slug
 * @returns {string} URL-friendly slug
 */
export const createSlug = (str: string): string => {
    return str
        .toLowerCase()
        .replace(/[^\w\s-]/g, '') // Remove non-word chars
        .replace(/\s+/g, '-')     // Replace spaces with hyphens
        .replace(/--+/g, '-')     // Replace multiple hyphens with single hyphen
        .trim();
};

/**
 * Safe JSON parse with error handling
 * @param {string} str - JSON string to parse
 * @param {any} fallback - Fallback value if parsing fails
 * @returns {any} Parsed object or fallback
 */
export const safeJsonParse = (str: string, fallback: any = {}): any => {
    try {
        return JSON.parse(str);
    } catch (error) {
        return fallback;
    }
};

/**
 * Get browser/device info for logging purposes
 * @returns {Object} Browser and device information
 */
export const getDeviceInfo = (): {
    userAgent: string;
    platform: string;
    screenWidth: number;
    screenHeight: number;
    language: string;
} => {
    if (typeof window === 'undefined') {
        return {
            userAgent: '',
            platform: '',
            screenWidth: 0,
            screenHeight: 0,
            language: ''
        };
    }

    return {
        userAgent: window.navigator.userAgent,
        platform: window.navigator.platform,
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        language: window.navigator.language
    };
};

/**
 * Get current user's local date and time in specified format
 * @param {string} format - Output format (default ISO)
 * @returns {string} Formatted current date and time
 */
export const getCurrentDateTime = (format = 'ISO'): string => {
    const now = new Date();

    if (format === 'ISO') {
        return now.toISOString();
    }

    if (format === 'YYYY-MM-DD HH:mm:ss') {
        return formatDate(now, 'YYYY-MM-DD HH:mm:ss');
    }

    return now.toString();
};
